import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react'
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll'

const FAQS = [
  {
    question: 'Como funciona o teste grátis de 7 dias?',
    answer: 'Ao se cadastrar, você usa o Premium completo por 7 dias, sem cartão de crédito. Se não assinar ao final, não há cobrança e tudo que você criou continua salvo.',
  },
  {
    question: 'Posso cancelar minha assinatura quando quiser?',
    answer: 'Sim, a qualquer momento. Sem contratos longos, sem taxa de cancelamento. A assinatura permanece ativa até o fim do período pago.',
  },
  {
    question: 'Como o banco de questões funciona?',
    answer: 'Você filtra por disciplina, série, tópico e dificuldade. Cada questão pode ser editada antes de incluir na sua avaliação.',
  },
  {
    question: 'O PDF gerado funciona em qualquer impressora?',
    answer: 'Sim. Oferecemos A4 e Carta, retrato ou paisagem, com ajuste de margens. Sem incompatibilidades de impressão.',
  },
  {
    question: 'O que acontece com minhas questões se eu cancelar?',
    answer: 'Suas questões e avaliações permanecem salvas e seu acesso continua até o fim do período já pago. Se voltar a assinar, encontra tudo onde deixou.',
  },
]

const FaqSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const revealRef = useRevealOnScroll<HTMLDivElement>()

  return (
    <section
      id="faq"
      className="py-20 bg-cream-50 dark:bg-ink-700"
      aria-labelledby="faq-titulo"
    >
      <div ref={revealRef} className="container mx-auto px-4">
        <div data-reveal-item className="text-center mb-16">
          <p className="flex items-center justify-center gap-3 font-mono text-caderno-xs uppercase tracking-widest text-ink-400 dark:text-ink-300 mb-4">
            <span className="text-terra-500">07</span>
            <span aria-hidden="true" className="w-8 h-px bg-cream-400 dark:bg-ink-500" />
            Dúvidas
          </p>
          <h2
            id="faq-titulo"
            className="text-3xl md:text-4xl font-display text-ink-900 dark:text-cream-50 mb-4"
          >
            O que professores perguntam{' '}
            <span className="italic font-normal text-terra-500">antes de começar</span>.
          </h2>
          <p className="text-xl text-ink-500 dark:text-cream-300 max-w-3xl mx-auto">
            Direto ao ponto, sem letra miúda.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <div
                key={faq.question}
                data-reveal-item
                style={{ '--reveal-delay': `${index * 60}ms` } as React.CSSProperties}
                className="bg-surface dark:bg-ink-900 rounded-caderno-sm overflow-hidden border border-cream-400 dark:border-ink-500"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-cream-100 dark:hover:bg-ink-700 focus:outline-none focus:ring-2 focus:ring-terra-300 transition-colors"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-lg font-display text-ink-900 dark:text-cream-50 pr-4">{faq.question}</h3>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-ink-500 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-ink-500 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
                {isOpen && (
                  <div id={`faq-answer-${index}`} className="px-6 pt-3 pb-6">
                    <p className="text-ink-500 dark:text-cream-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-ink-500 dark:text-cream-300 mb-6">
            Tem outra dúvida? Veja todas as perguntas no help center.
          </p>
          <Link
            to="/ajuda"
            className="px-6 py-3 bg-ink-900 dark:bg-ink-900 hover:bg-ink-700 text-cream-50 rounded-caderno-xs font-medium transition-colors inline-flex items-center focus:outline-none focus:ring-2 focus:ring-terra-300"
          >
            Ver todas as perguntas <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  )
}

export default FaqSection
